/**
 * @webready/shared/config
 * 
 * Centralized platform configuration.
 * All hardcoded values (brand, legal, schema names, storage keys, defaults)
 * are stored in JSON files grouped by category and re-exported here with types.
 * 
 * To customize the platform for a new client, edit ONLY the JSON files in this folder.
 * Zero code changes required in components, services, or controllers.
 */

import branding from './branding.json';
import legal from './legal.json';
import legalDocuments from './legal-documents.json';
import storage from './storage.json';
import database from './database.json';
import defaults from './defaults.json';
import maps from './maps.json';

// ─── Re-export JSON configs as typed constants ───────────────────────────────

/** Brand identity: app name, domain, contact email, cookie prefix, SEO defaults, placeholders */
export const BRANDING = branding;

/** Legal operator data: name, CUI, trade register, address, routes, external links */
export const LEGAL = legal;

/** Legal document templates with {{placeholder}} interpolation */
export const LEGAL_DOCUMENTS = legalDocuments;

/** localStorage / sessionStorage key names */
export const STORAGE = storage;

/** Database schema names, exposed schemas, table name mapping */
export const DATABASE = database;

/** Default values: country, language, currency, theme colors */
export const DEFAULTS = defaults;

/** Google Maps default embed configuration */
export const MAPS = maps;

// ─── Helper Functions ────────────────────────────────────────────────────────

/** Check if the given schema is the admin/master schema */
export const isAdminSchema = (schema: string): boolean =>
  schema === DATABASE.adminSchema;

/** Check if the given schema is a client (non-admin) tenant schema */
export const isClientSchema = (schema: string): boolean =>
  schema.startsWith(DATABASE.schemaPrefix) && !isAdminSchema(schema);

/** Get schema with fallback to admin schema */
export const getSchemaFallback = (envSchema?: string): string =>
  envSchema || DATABASE.adminSchema;

/** Get primary color with fallback to default */
export const getDefaultColor = (themeColor?: string | null): string =>
  themeColor || DEFAULTS.primaryColor;

/** Get secondary color with fallback to default */
export const getDefaultSecondaryColor = (themeColor?: string | null): string =>
  themeColor || DEFAULTS.secondaryColor;

/**
 * Interpolate a legal document template with operator/brand data.
 * Replaces {{appName}}, {{operatorName}}, {{operatorCui}}, etc.
 */
export const interpolateLegalTemplate = (
  template: string,
  overrides?: Partial<typeof legal.operator & { appName: string; jurisdiction: string }>
): string => {
  const op = { ...legal.operator, ...overrides };
  const appName = overrides?.appName ?? branding.appName;
  const jurisdiction = overrides?.jurisdiction ?? legal.jurisdiction;

  return template
    .replace(/\{\{appName\}\}/g, appName)
    .replace(/\{\{operatorName\}\}/g, op.name)
    .replace(/\{\{operatorShortName\}\}/g, op.shortName)
    .replace(/\{\{operatorCui\}\}/g, op.cui)
    .replace(/\{\{operatorTradeRegister\}\}/g, op.tradeRegister)
    .replace(/\{\{operatorAddress\}\}/g, op.address)
    .replace(/\{\{operatorEmail\}\}/g, op.email)
    .replace(/\{\{jurisdiction\}\}/g, jurisdiction);
};

// ─── TypeScript Types ────────────────────────────────────────────────────────

export type BrandingConfig = typeof branding;
export type LegalConfig = typeof legal;
export type LegalDocumentsConfig = typeof legalDocuments;
export type StorageConfig = typeof storage;
export type DatabaseConfig = typeof database;
export type DefaultsConfig = typeof defaults;
export type MapsConfig = typeof maps;
